import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ResponsiveLogo } from '../components/ThemeLogo';
import PublicHeader from '../components/PublicHeader';
import {
  Clock, ArrowLeft, ArrowRight, User, Calendar,
  BookOpen, Share2, Twitter, Linkedin, Link2, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

function TableOfContents({ body }) {
  const [headings, setHeadings] = useState([]);
  useEffect(() => {
    if (!body) return;
    const regex = /<h([2-3])[^>]*>(.*?)<\/h[2-3]>/gi;
    const found = [];
    let match;
    while ((match = regex.exec(body)) !== null) {
      const text = match[2].replace(/<[^>]+>/g, '');
      const id = text.toLowerCase().replace(/[^\w]+/g, '-');
      found.push({ level: parseInt(match[1]), text, id });
    }
    setHeadings(found);
  }, [body]);

  if (headings.length < 2) return null;
  return (
    <nav className="rounded-xl border border-border bg-card p-5 mb-8" data-testid="table-of-contents">
      <h3 className="font-semibold text-sm mb-3 uppercase tracking-wider text-muted-foreground">Table of Contents</h3>
      <ul className="space-y-1.5">
        {headings.map((h, i) => (
          <li key={i} style={{ paddingLeft: h.level === 3 ? '1rem' : 0 }}>
            <a href={`#${h.id}`} className="text-sm text-muted-foreground hover:text-foreground transition-colors block py-0.5">
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function ShareButtons({ title, slug }) {
  const url = `${window.location.origin}/blog/${slug}`;
  const copyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied!');
  };
  return (
    <div className="flex items-center gap-2" data-testid="share-buttons">
      <span className="text-xs text-muted-foreground mr-1">Share</span>
      <button onClick={copyLink} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Copy link"><Link2 className="w-4 h-4" /></button>
      <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-muted transition-colors"><Twitter className="w-4 h-4" /></a>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-muted transition-colors"><Linkedin className="w-4 h-4" /></a>
    </div>
  );
}

function RelatedArticles({ articles }) {
  if (!articles || articles.length === 0) return null;
  return (
    <section className="mt-16 pt-12 border-t border-border" data-testid="related-articles">
      <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {articles.map(post => (
          <Link key={post.blog_id} to={`/blog/${post.slug}`} className="group block">
            <div className="rounded-xl overflow-hidden border border-border bg-card hover:shadow-lg transition-all">
              <div className="aspect-[16/10] bg-muted overflow-hidden">
                {post.featured_image ? (
                  <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-odapto-orange/10 to-odapto-teal/10 flex items-center justify-center">
                    <BookOpen className="w-8 h-8 text-odapto-orange/30" />
                  </div>
                )}
              </div>
              <div className="p-4">
                <span className="text-[11px] font-semibold text-odapto-orange uppercase tracking-wider">{post.category}</span>
                <h3 className="font-bold mt-1 group-hover:text-odapto-orange transition-colors line-clamp-2">{post.title}</h3>
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" /> {post.reading_time} min read
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function addHeadingIds(html) {
  if (!html) return '';
  return html.replace(/<h([2-3])([^>]*)>(.*?)<\/h[2-3]>/gi, (match, level, attrs, content) => {
    const text = content.replace(/<[^>]+>/g, '');
    const id = text.toLowerCase().replace(/[^\w]+/g, '-');
    return `<h${level}${attrs} id="${id}">${content}</h${level}>`;
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [prevPost, setPrevPost] = useState(null);
  const [nextPost, setNextPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${slug}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        setPost(data);
        setRelated(data.related || []);
        setPrevPost(data.prev_post);
        setNextPost(data.next_post);
        window.scrollTo(0, 0);
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-10 h-10 border-4 border-odapto-orange border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!post) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Article Not Found</h1>
        <p className="text-muted-foreground mb-4">This article may have been removed or is not yet published.</p>
        <Link to="/blog"><Button className="bg-odapto-orange hover:bg-odapto-orange-hover text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      <PublicHeader />

      {/* Hero Image */}
      {post.featured_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8" data-testid="blog-hero-image">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-auto rounded-2xl bg-muted block"
            style={{ maxWidth: '100%', objectFit: 'contain', objectPosition: 'center' }}
          />
        </div>
      )}

      {/* Article */}
      <article className="max-w-[820px] mx-auto px-4 sm:px-6 py-10" data-testid="blog-article">
        {/* Meta */}
        <div className="mb-8">
          <Link to="/blog" className="text-sm text-odapto-orange hover:underline inline-flex items-center gap-1 mb-5">
            <ArrowLeft className="w-3 h-3" /> Blog
          </Link>
          <span className="block text-xs font-semibold text-odapto-orange uppercase tracking-wider mb-3">{post.category}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold mb-5 leading-[1.15] tracking-tight">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-5">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author_name}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {post.publish_date ? new Date(post.publish_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.reading_time} min read</span>
          </div>
          <ShareButtons title={post.title} slug={post.slug} />
        </div>

        {/* ToC */}
        <TableOfContents body={post.body} />

        {/* Body */}
        <div
          className="prose prose-lg dark:prose-invert prose-headings:scroll-mt-20 prose-headings:tracking-tight prose-headings:leading-tight prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:leading-[1.8] prose-p:mb-5 prose-a:text-odapto-orange prose-img:rounded-xl prose-img:mx-auto prose-blockquote:border-odapto-orange/40 prose-blockquote:bg-muted/30 prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:not-italic max-w-none"
          style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: addHeadingIds(post.body) }}
          data-testid="blog-post-body"
        />

        {/* Bottom Share */}
        <div className="mt-12 pt-6 border-t border-border flex items-center justify-between flex-wrap gap-3">
          <div className="flex flex-wrap gap-2">
            {(post.tags || []).map(tag => (
              <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{tag}</span>
            ))}
          </div>
          <ShareButtons title={post.title} slug={post.slug} />
        </div>

        {/* Prev / Next */}
        {(prevPost || nextPost) && (
          <div className="grid sm:grid-cols-2 gap-4 mt-10 pt-8 border-t border-border" data-testid="prev-next-nav">
            {prevPost ? (
              <Link to={`/blog/${prevPost.slug}`} className="p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors group">
                <span className="text-xs text-muted-foreground flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Previous</span>
                <p className="font-semibold mt-1 group-hover:text-odapto-orange transition-colors line-clamp-1">{prevPost.title}</p>
              </Link>
            ) : <div />}
            {nextPost ? (
              <Link to={`/blog/${nextPost.slug}`} className="p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors text-right group">
                <span className="text-xs text-muted-foreground flex items-center justify-end gap-1">Next <ArrowRight className="w-3 h-3" /></span>
                <p className="font-semibold mt-1 group-hover:text-odapto-orange transition-colors line-clamp-1">{nextPost.title}</p>
              </Link>
            ) : <div />}
          </div>
        )}

        {/* Newsletter CTA */}
        <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-odapto-orange/10 to-odapto-teal/10 border border-odapto-orange/20 text-center" data-testid="newsletter-cta">
          <h3 className="text-xl font-bold mb-2">Stay ahead of the curve</h3>
          <p className="text-muted-foreground text-sm mb-4">Get the latest strategic insights delivered to your inbox.</p>
          <Link to="/register">
            <Button className="bg-odapto-orange hover:bg-odapto-orange-hover text-white px-6">
              Join Odapto <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Related Articles */}
        <RelatedArticles articles={related} />
      </article>

      {/* Footer */}
      <footer className="bg-[#162F38] text-zinc-400 py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/">
            <ResponsiveLogo className="h-7 w-auto brightness-200" />
          </Link>
          <span className="text-xs">&copy; 2026 Odapto. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
